import { RANK_ART, REWARDS_ART } from '@/assets/rewards';
import type { LeaderboardMetric } from '@/lib/api/rewards-leaderboard/models';

/**
 * Visual system of the Rewards Hub leaderboard, transcribed from Figma
 * 13449:466421. Gradients are written as CSS so a component never has to inline
 * the SVG paint Figma exports.
 */

/** Podium, crown and confetti behind the page title (Figma image 6874). */
export const PODIUM_ART = REWARDS_ART['leaderboard-podium.webp'];

/**
 * Light behind the podium (Figma 13449:466430 / 13449:466431), as shares of the
 * podium's 687×458 box so it scales with the art: a gold haze tilted -20°, at
 * 10%, and a warm beam falling on the podium, at 16%.
 */
export const PODIUM_HAZE =
  'radial-gradient(50% 50% at 50% 50%, rgba(174,133,0,1) 0%, rgba(255,197,7,0) 100%)';
export const PODIUM_BEAM =
  'linear-gradient(181.15deg, #713a00 22.8%, rgba(179,103,21,0.71) 43%, rgba(0,0,0,0) 93.2%)';
/** The beam's trapezoid: narrow at the top, spreading down onto the podium. */
export const PODIUM_BEAM_SHAPE = 'polygon(29.93% 0, 75.86% 0, 100% 100%, 0 95.73%)';

/** Laurel wreath framing every category winner (Figma image 7002). */
export const WREATH_ART = REWARDS_ART['leaderboard-wreath.webp'];

/**
 * The coin each category winner stands on. Figma keeps all five in one strip
 * and slides a 46px window along it; they are sliced into files here so a card
 * can size its coin freely.
 */
export const METRIC_MEDAL: Record<LeaderboardMetric, string> = {
  volume: REWARDS_ART['leaderboard-medal-volume.webp'],
  trades: REWARDS_ART['leaderboard-medal-trades.webp'],
  bestDeal: REWARDS_ART['leaderboard-medal-best-deal.webp'],
  roe: REWARDS_ART['leaderboard-medal-roe.webp'],
  tasks: REWARDS_ART['leaderboard-medal-tasks.webp'],
};

/**
 * Gold beam Figma lays behind each winner column: a horizontal band that
 * blooms in the middle of the column and fades at both edges.
 */
export const WINNER_BEAM =
  'linear-gradient(89.29deg, rgba(96,64,0,0) 2.7%, rgb(175,117,0) 49.8%, rgba(96,64,0,0) 99.3%)';

/**
 * The monthly prize (Figma 14308:46295, 09.10): the gift over each winner —
 * the header's Rewards Hub gift, the same art — its gold glow, and the
 * illustration that tops the prize card.
 */
export const PRIZE_GIFT_ART = REWARDS_ART['nav-gift.webp'];
export const PRIZE_GIFT_GLOW = 'drop-shadow(0 0 10px rgba(255,200,0,0.3))';
export const PRIZE_ILLUSTRATION = REWARDS_ART['leaderboard-prize-illustration.svg'];
/**
 * The prize card's body, Figma `surface/primary`: the illustration is drawn on
 * it and fades into it at the foot. The hub turns the page's own surface pure
 * black, which would leave a seam under the illustration (13.10).
 */
export const PRIZE_CARD_BACKGROUND = '#0e1011';
/** Figma `yellow/200`: the prize's own words, on the card and on the timer. */
export const PRIZE_TEXT = '#ffb368';
/** The timer pill: a dark wine body under a dim gold rim. */
export const PRIZE_TIMER_BACKGROUND = '#300101';
export const PRIZE_TIMER_BORDER = '#3f2d01';

/** Ring around a winner's avatar inside the wreath. */
export const WINNER_AVATAR_BORDER = '#955900';

/**
 * The empty disc in the wreath of a category nobody leads (Figma 14760:46553):
 * slate lit from the middle, under a dark rim.
 */
export const NO_WINNER_DISC = 'radial-gradient(closest-side, #4a5054, #121617)';
export const NO_WINNER_DISC_BORDER = '#2a3032';

/** Warm border of the winners panel. */
export const WINNERS_PANEL_BORDER = '#624300';

/** "You" pill on the player's own row — blue running from the right. */
export const YOU_PILL_BACKGROUND = 'linear-gradient(264.7deg, #005ae0 4.5%, #00317a 99.8%)';

/**
 * Dotted rule under a rank title, marking it as the thing that opens the rank
 * breakdown. Figma strokes it white 35% with a 1.5/2 dash, which CSS `dashed`
 * rounds to something coarser — hence the explicit repeat.
 */
export const RANK_UNDERLINE =
  'repeating-linear-gradient(to right, rgba(255,255,255,0.35) 0 1.5px, transparent 1.5px 3.5px)';

/** Cold light Figma pools down the page behind the table (Ellipse 27263/27264). */
export const PAGE_GLOW =
  'radial-gradient(50% 50% at 50% 50%, rgba(11,113,255,0.18) 0%, rgba(11,113,255,0.05) 55%, transparent 100%)';

/** Rank art is the same set Climb the Ranks draws, keyed by the same index. */
export function rankArt(rankIndex: number): string {
  return RANK_ART[Math.min(6, Math.max(1, rankIndex)) - 1];
}

/**
 * Feathers the podium clip's frame into the page (07.10): the light runs out
 * of its top edge and the ribbons out of its sides, and the section clips the
 * frame where it overhangs, so every edge fades out before it can be cut.
 */
const PODIUM_CLIP_FADE = [
  'linear-gradient(to right, transparent, black 7%, black 93%, transparent)',
  'linear-gradient(to bottom, transparent, black 12%, black 93%, transparent)',
].join(', ');

export const PODIUM_CLIP_MASK = {
  maskImage: PODIUM_CLIP_FADE,
  WebkitMaskImage: PODIUM_CLIP_FADE,
  maskComposite: 'intersect',
  WebkitMaskComposite: 'source-in',
} as const;
